import { useEffect, useRef, useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { auth } from '../firebase';
import { setEvents, setListening, setError, clearAllEvents } from '../store/realtimeSlice';
import { setNotifications, resetNotifications } from '../store/notificationsSlice';
import { setPresences, resetPresence } from '../store/presenceSlice';
import { setBrandData, setBrandListening, resetBrand } from '../store/brandSlice';
import { setPermissions, setLabels, setCustomizationsListening, resetCustomizations } from '../store/customizationsSlice';
import { setTimesheetTimer, resetTimesheetTimer } from '../store/timesheetTimerSlice';
import { setCoreSeqJobs, resetCoreSeqJobs } from '../store/coreSeqJobsSlice';
import { updateCoreSeqJobsCache } from '../utils/coreSeqJobsBridge';
import { createSessionActivityTracker, SESSION_IDLE_MS } from '../utils/sessionActivity';

const WS_URL = process.env.REACT_APP_REALTIME_WS_URL || 'wss://realtime.usenode.com';
const HEARTBEAT_INTERVAL = 25000;
const RECONNECT_BASE = 1000;
const RECONNECT_MAX = 30000;

export default function useRealtimeSocket(businessId) {
  const dispatch = useDispatch();
  const socketRef = useRef(null);
  const heartbeatRef = useRef(null);
  const reconnectRef = useRef(null);
  const reconnectAttempt = useRef(0);
  const mountedRef = useRef(true);
  const activityRef = useRef(null);
  const sessionReadyRef = useRef(false);
  const connectionGenerationRef = useRef(0);
  const [connected, setConnected] = useState(false);
  const [authUserId, setAuthUserId] = useState(auth.currentUser?.uid || null);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setAuthUserId(user?.uid || null);
    });
    return () => unsubscribe();
  }, []);

  const cleanup = useCallback(() => {
    connectionGenerationRef.current += 1;
    sessionReadyRef.current = false;
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
    if (reconnectRef.current) {
      clearTimeout(reconnectRef.current);
      reconnectRef.current = null;
    }
    if (socketRef.current) {
      socketRef.current.onclose = null;
      socketRef.current.close();
      socketRef.current = null;
    }
    setConnected(false);
  }, []);

  const getActivity = useCallback(() => activityRef.current?.getState() || {
    visible: false,
    idleMs: SESSION_IDLE_MS,
  }, []);

  const connect = useCallback(async () => {
    cleanup();
    if (!businessId || !authUserId || auth.currentUser?.uid !== authUserId) return;
    const generation = connectionGenerationRef.current;

    try {
      const token = await auth.currentUser.getIdToken();
      if (!mountedRef.current || generation !== connectionGenerationRef.current || auth.currentUser?.uid !== authUserId) return;
      const businessToken = localStorage.getItem('businessToken');
      if (!token || !businessToken) return;

      const socket = new WebSocket(WS_URL);
      socketRef.current = socket;

      socket.onopen = () => {
        if (socketRef.current !== socket) return;
        socket.send(JSON.stringify({
          type: 'auth',
          token,
          businessToken,
          activity: getActivity(),
        }));
      };

      socket.onmessage = (event) => {
        if (socketRef.current !== socket) return;
        let msg;
        try {
          msg = JSON.parse(event.data);
        } catch {
          return;
        }

        switch (msg.type) {
          case 'init':
            sessionReadyRef.current = true;
            setConnected(true);
            reconnectAttempt.current = 0;
            dispatch(setListening(true));
            dispatch(setError(null));

            if (heartbeatRef.current) clearInterval(heartbeatRef.current);
            socket.send(JSON.stringify({ type: 'activity', activity: getActivity() }));
            heartbeatRef.current = setInterval(() => {
              if (socket.readyState === WebSocket.OPEN) {
                socket.send(JSON.stringify({ type: 'ping', activity: getActivity() }));
              }
            }, HEARTBEAT_INTERVAL);
            break;

          case 'notifications:snapshot':
            dispatch(setNotifications(msg.notifications || []));
            break;

          case 'notifications:update':
            dispatch(setNotifications(msg.notifications || []));
            break;

          case 'events:snapshot':
          case 'events':
            dispatch(setEvents(msg.events || {}));
            break;

          case 'presence:snapshot':
          case 'presence':
            dispatch(setPresences(msg.presences || {}));
            break;

          case 'brand:snapshot':
          case 'brand':
            dispatch(setBrandData(msg.brand || {}));
            dispatch(setBrandListening(true));
            break;

          case 'customizations:permissions:snapshot':
          case 'customizations:permissions':
            dispatch(setPermissions(msg.permissions || {}));
            dispatch(setCustomizationsListening(true));
            break;

          case 'customizations:labels:snapshot':
          case 'customizations:labels':
            dispatch(setLabels(msg.labels || {}));
            break;

          case 'timesheet:timer:snapshot':
          case 'timesheet:timer':
            dispatch(setTimesheetTimer(msg.timer || null));
            break;

          case 'coreSeqJobs:snapshot':
          case 'coreSeqJobs':
            dispatch(setCoreSeqJobs(msg.jobs || []));
            updateCoreSeqJobsCache(msg.jobs || []);
            break;

          case 'pong':
            break;

          default:
            break;
        }
      };

      socket.onclose = () => {
        if (socketRef.current !== socket) return;
        sessionReadyRef.current = false;
        if (heartbeatRef.current) clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;
        setConnected(false);
        dispatch(setListening(false));

        if (!mountedRef.current) return;

        const delay = Math.min(
          RECONNECT_BASE * Math.pow(2, reconnectAttempt.current),
          RECONNECT_MAX
        );
        reconnectAttempt.current += 1;
        reconnectRef.current = setTimeout(() => {
          if (mountedRef.current) connect();
        }, delay);
      };

      socket.onerror = () => {
        socket.close();
      };
    } catch (err) {
      console.error('[useRealtimeSocket] Connection error:', err.message);
    }
  }, [businessId, authUserId, cleanup, dispatch, getActivity]);

  useEffect(() => {
    if (!authUserId || !businessId) return undefined;
    const tracker = createSessionActivityTracker({
      userId: authUserId,
      onChange: (activity) => {
        const socket = socketRef.current;
        if (sessionReadyRef.current && socket?.readyState === WebSocket.OPEN) {
          socket.send(JSON.stringify({ type: 'activity', activity }));
        }
      },
    });
    activityRef.current = tracker;
    return () => {
      tracker.stop();
      activityRef.current = null;
    };
  }, [authUserId, businessId]);

  useEffect(() => {
    mountedRef.current = true;
    connect();

    return () => {
      mountedRef.current = false;
      cleanup();
      dispatch(setListening(false));
      dispatch(clearAllEvents());
      dispatch(resetNotifications());
      dispatch(resetPresence());
      dispatch(resetBrand());
      dispatch(resetCustomizations());
      dispatch(resetTimesheetTimer());
      dispatch(resetCoreSeqJobs());
      updateCoreSeqJobsCache([]);
    };
  }, [connect, cleanup, dispatch]);

  const setPath = useCallback((pathId) => {
    const socket = socketRef.current;
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify({ type: 'setPath', pathId }));
    }
  }, []);

  return { connected, setPath };
}
